import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { Haptics } from '@capacitor/haptics';
import { NavigationEnd, Router, RouterOutlet, Scroll } from '@angular/router';
import { NavbarComponent } from './core/components/navbar/navbar';
import { filter, map } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { KeyboardScrollService } from './core/providers/services/keyboard-scroll.service';

import { PushNotificationService } from './core/providers/services/push-notification.service';
import { LayoutService } from './core/providers/services/layout.service';
import { SeasonRecapComponent } from './feature/ranking/season-recap/season-recap';
import { SeasonRecapService } from './core/providers/services/season-recap.service';

@Component({
  selector: 'ootd-root',
  imports: [RouterOutlet, NavbarComponent, ToastModule, ConfirmDialogModule, SeasonRecapComponent],
  templateUrl: './app.html',
  styleUrl: './app.less',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class App {
  private readonly router = inject(Router);
  private readonly keyboardScrollService = inject(KeyboardScrollService);
  readonly layoutService = inject(LayoutService);

  constructor() {
    this.keyboardScrollService.init();

    this.loadHaptics();

    this.router.events.pipe(
      filter((event): event is Scroll => event instanceof Scroll)
    ).subscribe((event: Scroll) => {
      if (event.anchor) {
        setTimeout(() => this.scrollToSection(event.anchor!), 500);
      }
    });
  }

  private loadHaptics() {
    Haptics.selectionStart().then(() => Haptics.selectionEnd()).catch(() => { });
  }

  private readonly navigationEnd = toSignal(
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map((event) => event as NavigationEnd)
    )
  );

  protected readonly hideNavbar = computed(() => {
    const url = this.navigationEnd()?.urlAfterRedirects ?? this.router.url ?? '';
    return url.startsWith('/login') || url.startsWith('/onboarding') || url.startsWith('/admin') || url.includes('premium') || url.includes('settings');
  });

  private scrollToSection(sectionId: string) {
    const targetElement = document.getElementById(sectionId);
    const scrollContainer = document.querySelector('.section-layout--scrolling-page__content');

    if (targetElement && scrollContainer) {
      this.scrollWithinContainer(targetElement, scrollContainer);
    } else if (targetElement) {
      targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  private scrollWithinContainer(targetElement: Element, scrollContainer: Element) {
    const containerBoundingRect = scrollContainer.getBoundingClientRect();
    const targetElementBoundingRect = targetElement.getBoundingClientRect();

    const targetScrollPosition = targetElementBoundingRect.top - containerBoundingRect.top + scrollContainer.scrollTop;

    scrollContainer.scrollTo({
      top: targetScrollPosition,
      behavior: 'smooth'
    });
  }
}
